import { atom } from "jotai";
import { queryClientAtom } from "jotai-tanstack-query";

import type {
  PickerTokenResponse,
  SupportEmailResendResponse,
  Settings,
  SettingsResponse,
} from "@go-novus/admin-api";

import {
  createResourceCache,
  createResourceMutationAtom,
  createResourceQueryAtom,
  defineResource,
  defineResourceMutation,
  defineResourceQuery,
} from "#/shared/lib/resource";

import {
  createPickerToken,
  createPickerTokenOperation,
  type CreatePickerTokenFailure,
} from "../api/create-picker-token";
import {
  resendSupportEmail,
  resendSupportEmailOperation,
  type ResendSupportEmailFailure,
} from "../api/resend-support-email";
import { getSettings } from "../api/get-settings";
import {
  putSettings,
  putSettingsOperation,
  type PutSettingsFailure,
} from "../api/put-settings";
import {
  saveSettingsLogo,
  saveSettingsLogoOperation,
  type SaveSettingsLogoFailure,
} from "../api/save-settings-logo";

const settingsResource = defineResource("settings");

const settingsQuery = defineResourceQuery({
  key: () => [],
  name: "detail",
  queryFn: () => getSettings(),
  resource: settingsResource,
  staleTime: 60_000,
});

/** 已保存的配置。页面拿它做草稿的初值，也拿它判断有没有未保存修改。 */
export const settingsAtom = createResourceQueryAtom(
  settingsQuery,
  () => undefined,
);

/**
 * 把已保存配置标成过期：拾取会话随页面下台结束时调用——staleTime 期内
 * 缓存仍是新鲜的，不标过期的话回 Settings 那次挂载会拿旧值当最新。
 */
export const markSettingsStaleAtom = atom(null, (get) =>
  createResourceCache(get(queryClientAtom)).invalidate(settingsQuery),
);

/** 保存成功后作废已保存配置的缓存，页面据此把草稿重置回新值。 */
export const saveSettingsAtom = createResourceMutationAtom<
  SettingsResponse,
  Settings,
  PutSettingsFailure
>(
  defineResourceMutation({
    mutationFn: (settings: Settings) => putSettings(settings),
    operation: putSettingsOperation,
    resource: settingsResource,
    invalidates: [settingsResource],
  }),
);

/**
 * 为已保存的元素可见规则签发拾取令牌。签发本身不改配置，
 * 不作废缓存；店面写回由页面在商家返回时重新读取。
 */
export const pickElementAtom = createResourceMutationAtom<
  PickerTokenResponse,
  string,
  CreatePickerTokenFailure
>(
  defineResourceMutation({
    invalidates: [],
    mutationFn: (ruleId: string) => createPickerToken(ruleId),
    operation: createPickerTokenOperation,
    resource: settingsResource,
  }),
);

/** 传 Logo 或移除 Logo；`null` 是移除。成功后作废配置缓存，读回新的 logoUrl。 */
export const saveLogoAtom = createResourceMutationAtom<
  SettingsResponse,
  File | null,
  SaveSettingsLogoFailure
>(
  defineResourceMutation({
    mutationFn: (file: File | null) => saveSettingsLogo(file),
    operation: saveSettingsLogoOperation,
    resource: settingsResource,
    invalidates: [settingsResource],
  }),
);

export const resendSupportEmailAtom = createResourceMutationAtom<
  SupportEmailResendResponse,
  void,
  ResendSupportEmailFailure
>((get) =>
  defineResourceMutation({
    mutationFn: () =>
      resendSupportEmail().map((response) => {
        get(queryClientAtom).setQueryData(
          settingsQuery.getKey(undefined),
          response.settings,
        );
        return response;
      }),
    operation: resendSupportEmailOperation,
    resource: settingsResource,
    invalidates: [],
  }),
);
